import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Button, Spinner, toast } from '@/components/ui'
import { useSession } from '@/features/auth/session'
import { useAcceptInvitation } from '@/features/team/queries'

import { storePendingInvite } from './pendingInvite'

/**
 * Pagina publica de ACEITE de convite (rota /invite/$token). Se o convidado nao esta logado, guarda o
 * token e manda para o login (que volta aqui apos autenticar); se esta logado, aceita e vai para o
 * dashboard do tenant. O token so trafega no corpo do POST (nunca em query/log).
 */
export function AcceptInvitePage({ token }: { readonly token: string }) {
  const { t } = useTranslation()
  const { user, isLoading } = useSession()
  const navigate = useNavigate()
  const accept = useAcceptInvitation()
  const attempted = useRef(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (isLoading || attempted.current) {
      return
    }
    attempted.current = true

    if (user === null) {
      storePendingInvite(token)
      void navigate({ to: '/login' })
      return
    }

    accept.mutate(
      { token },
      {
        onSuccess: (result) => {
          toast.success(
            result.alreadyMember ? t('acceptInvite.alreadyMember') : t('acceptInvite.success'),
          )
          void navigate({
            to: '/app/$tenantSlug/dashboard',
            params: { tenantSlug: result.tenantSlug },
          })
        },
        onError: (error) => {
          setErrorMessage(
            error instanceof ApiError ? error.problem.title : t('acceptInvite.genericError'),
          )
        },
      },
    )
  }, [isLoading, user, token, navigate, accept, t])

  if (errorMessage !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="flex w-full max-w-md flex-col gap-4">
          <Alert variant="danger" title={t('acceptInvite.errorTitle')}>
            {errorMessage}
          </Alert>
          <Button
            variant="secondary"
            onClick={() => {
              void navigate({ to: '/' })
            }}
          >
            {t('acceptInvite.backHome')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Spinner label={t('acceptInvite.working')} />
    </div>
  )
}
